import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { Video, Upload, Trash2, ExternalLink, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Teacher → "Recordings" section.
 * Lists the teacher's completed classes and lets them attach a recording
 * (mp4/webm/mov, up to 100 MB) via Emergent Object Storage.
 * Honours the student's recording_disabled opt-out — backend returns 403.
 */
const TeacherRecordingsSection = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRefs = useRef({});

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/teacher/completed-classes?days=30');
      setClasses(res.data || []);
    } catch (e) {
      toast.error('Failed to load completed classes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFilePick = (classId) => {
    fileInputRefs.current[classId]?.click();
  };

  const handleUpload = async (classId, file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File exceeds 100 MB limit');
      return;
    }
    setUploadingId(classId);
    try {
      // 1. Upload the file
      const fd = new FormData();
      fd.append('file', file);
      fd.append('context', 'class-recording');
      fd.append('ref_id', classId);
      const up = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // 2. Attach to class
      await api.post(`/class/${classId}/recording`, { file_id: up.data.id });
      toast.success('Recording uploaded');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemove = async (classId) => {
    if (!confirm('Remove this recording? Students will no longer be able to rewatch.')) return;
    try {
      await api.delete(`/class/${classId}/recording`);
      toast.success('Recording removed');
      await load();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return <div className="glass-card rounded-xl p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="teacher-recordings-section">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Class Recordings</h1>
        <p className="text-sm text-slate-500 font-semibold">Upload session recordings so students can rewatch. Max 100 MB · mp4/webm/mov.</p>
      </div>

      {classes.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center">
          <Video className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500 font-semibold">No completed classes in the last 30 days.</p>
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="divide-y divide-slate-100">
            {classes.map(cls => {
              const optedOut = !!cls.student_recording_disabled;
              const hasRecording = !!cls.recording_url;
              return (
                <div key={cls.id} className="px-5 py-3 flex items-center gap-4" data-testid={`rec-row-${cls.id}`}>
                  <div className="w-14 text-center flex-shrink-0">
                    <div className="text-lg font-bold text-slate-900">{new Date(cls.date + 'T00:00:00').getDate()}</div>
                    <div className="text-[10px] text-slate-500 font-semibold uppercase">{new Date(cls.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 text-sm truncate">{cls.student_name}</p>
                    <p className="text-xs text-slate-500 font-semibold">{cls.time} {cls.timezone} · {cls.topic_name || 'Class'}</p>
                  </div>
                  {optedOut ? (
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1" title="Student has disabled recordings">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Recordings off
                    </div>
                  ) : hasRecording ? (
                    <div className="flex gap-2 items-center">
                      <a
                        href={cls.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-700 font-semibold hover:underline"
                        data-testid={`rec-open-${cls.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> View
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFilePick(cls.id)}
                        disabled={uploadingId === cls.id}
                        className="text-xs h-7"
                        data-testid={`rec-replace-${cls.id}`}
                      >
                        {uploadingId === cls.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Replace'}
                      </Button>
                      <button
                        onClick={() => handleRemove(cls.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                        title="Remove recording"
                        data-testid={`rec-remove-${cls.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleFilePick(cls.id)}
                      disabled={uploadingId === cls.id}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      data-testid={`rec-upload-${cls.id}`}
                    >
                      {uploadingId === cls.id ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…</>
                      ) : (
                        <><Upload className="w-3 h-3 mr-1" /> Upload Recording</>
                      )}
                    </Button>
                  )}
                  <input
                    ref={el => { if (el) fileInputRefs.current[cls.id] = el; }}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    onChange={e => handleUpload(cls.id, e.target.files?.[0])}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherRecordingsSection;
